import { Colors } from '../consts/Colors.js';
import AbstractScene from './AbstractScene.js';
import quizQuestions from '../data/QuizQuestions.js';
import quizData from '../data/Quiz.js';
import { Sizes } from '../consts/Sizes.js';
import { Fonts } from '../consts/Fonts.js';
import ButtonRounded from '../prefabs/ButtonRounded.js';
import Breadcrumb from '../prefabs/Breadcrumb.js';

export default class Quiz extends AbstractScene {
    constructor() {
        super({ key: 'Quiz' });
    }

    init(data) {
        this._init(data);
    }

    preload() {
        this._preload();
        this.currentQuestionIndex = 0;
        this.questionText = null;
        this.answerText = null;
        this.answerBackground = null;
        this.answersOptions = [];
        this.answersOptionsText = [];
        this.submitButton = null;
        this.totalQuestions = quizQuestions.length;
        this.breadcrumbPositionsY = [510, 510, 450, 550, 500, 550, 550];
    }

    update() {
        super.update();
    }

    create() {
        this.cameras.main.setBackgroundColor(Colors.Elmt.PaleWhite);
        this.cameras.main.setAlpha(0);
        this.tweens.add({
            targets: this.cameras.main,
            alpha: 1,
            duration: 500,
            ease: 'Power2',
            onComplete: () => {
                this.showIntro();
            },
        });
        this.add.image(0, 0, 'background').setOrigin(0);

        this.breadcrumb = new Breadcrumb(
            this,
            320,
            this.breadcrumbPositionsY[this.currentQuestionIndex],
            this.totalQuestions,
            this.currentQuestionIndex
        ).setAlpha(0);
        this.add.existing(this.breadcrumb);

        // Écoute le clic sur un dot
        this.breadcrumb.on('dotClicked', (index) => {
            this.goToQuestion(index);
        });
    }

    showIntro() {
        // Logo L'Oréal
        this.logo = this.add
            .image(this.width - 100, this.height - 50, 'logoLoreal')
            .setOrigin(0.5, 0.5)
            .setScale(0.5, 0.5);
        const introUI = [];
        const robotIntro = this.add
            .image(-400, this.height / 2, 'robotQuiz')
            .setOrigin(0, 0.5)
            .setScale(0);

        // ajouter fleches animées
        this.addAnimatedArrows();

        // Animate robot to final position
        this.tweens.add({
            targets: robotIntro,
            x: 400,
            scale: 1.5,
            duration: 500,
            ease: 'Back.Out',
        });

        const textIntro = this.add
            .text(this.width - 900, this.height / 2 - 250, quizData[0].quizText, {
                fontFamily: Fonts.Text.h3,
                fontSize: Sizes.Text.h2,
                fill: Colors.Text.White,
                wordWrap: { width: 600 },
            })
            .setAlpha(0);
        introUI.push(textIntro);

        // Bouton "Commencer"
        this.btnCommencer = this.add
            .existing(
                new ButtonRounded(
                    this,
                    this.width - 900 + 330 / 2,
                    this.height / 2 + 310,
                    330,
                    76,
                    quizData[0].buttonLabel,
                    Colors.Text.Blue,
                    Colors.Elmt.Yellow,
                    true,
                    Colors.Elmt.Yellow,
                    true,
                    false,
                    'fleche',
                    ''
                )
            )
            .setDepth(99)
            .setAlpha(0)
            .setScale(0);
        introUI.push(this.btnCommencer);

        // Animate robot to final position
        this.tweens.add({
            targets: textIntro,
            alpha: 1,
            duration: 600,
            ease: 'Back.Out',
            onComplete: () => {
                this.tweens.add({
                    targets: this.btnCommencer,
                    alpha: 1,
                    scale: 1,
                    duration: 300,
                    ease: 'Back.Out',
                });
            },
        });

        this.btnCommencer.on('pointerdown', () => {
            this.tweens.add({
                targets: introUI,
                alpha: 0,
                duration: 500,
                onComplete: () => {
                    introUI.forEach((item) => item.destroy());
                    this.btnCommencer.destroy();
                    this.logo.destroy();
                    this.showQuestions(quizQuestions[this.currentQuestionIndex]);
                },
            });

            this.tweens.add({
                targets: robotIntro,
                x: -robotIntro.width - 250, // Move to the left out of the screen
                duration: 500,
                ease: 'Power2',
            });
        });
    }

    showQuestions(question) {
        // Logo L'Oréal
        this.logo = this.add
            .image(this.width - 100, this.height - 50, 'logoLoreal')
            .setOrigin(0.5, 0.5)
            .setScale(0.5, 0.5);

        // add robot image
        // Add robot image off-screen first
        this.robotQuestion = this.add
            .image(-400, this.height + 400, 'robotQuestionQuiz') // Start off-screen at the bottom
            .setScale(0)
            .setOrigin(0, 0.5);

        // Animate robot to final position
        this.tweens.add({
            targets: this.robotQuestion,
            x: 250,
            y: this.height / 2 + 250,
            scale: 1,
            duration: 500,
            ease: 'Back.Out',
            onComplete: () => {
                this.tweens.add({
                    targets: this.breadcrumb,
                    y: this.breadcrumbPositionsY[this.currentQuestionIndex],
                    alpha: 1,
                    duration: 500,
                });
            },
        });

        this.breadcrumb.updateStep(this.currentQuestionIndex);

        // 👉 écouter le clic sur un dot
        this.breadcrumb.on('dotClicked', (index) => {
            this.goToQuestion(index);
        });

        this.QuestionIndexText = this.add
            .text(150, 200, `Question ${this.currentQuestionIndex + 1} `, {
                fontFamily: Fonts.Text.h3,
                fontSize: Sizes.Text.h3,
                fill: Colors.Text.White,
            })
            .setAlpha(0)
            .setOrigin(0, 0.5);

        // Create question text and answer options
        this.questionText = this.add
            .text(150, 250, question.question, {
                fontFamily: Fonts.Text.h2,
                fontSize: Sizes.Text.h2,
                fill: Colors.Text.Yellow,
                wordWrap: { width: 660 },
            })
            .setAlpha(0);

        // Inverse the order of the answers
        const totalAnswers = question.answers.length;
        const answersAreaHeight = totalAnswers * 150;
        const startY = this.height / 2 - answersAreaHeight / 2; // 75 centers the first answer

        const rectX = this.width - 997 - 100;
        const rectWidth = 997;
        const rectHeight = 106;
        this.quizConsigne = this.add
            .text(rectX + rectWidth / 2, startY - 50, quizData[0].quizConsigne, {
                fontFamily: Fonts.Text.h3,
                fontSize: Sizes.Text.p,
                fill: Colors.Text.Yellow,
                wordWrap: { width: 620 },
            })
            .setAlpha(0)
            .setOrigin(0.5, 0.5);

        // Show answers in reverse order: i = length-1 first, i = 0 last
        question.answers.forEach((answer, i) => {
            // Calculate reversed index
            const answerIndex = i;
            const rectY = startY + i * 150;

            this.answerBackground = this.add
                .rexRoundRectangle(
                    rectX,
                    rectY,
                    rectWidth,
                    rectHeight,
                    50, // corner radius
                    Colors.Elmt.White
                )
                .setAlpha(0)
                .setScale(0)
                .setOrigin(0, 0);
            this.answersOptions.push(this.answerBackground);

            this.answerText = this.add
                .text(
                    rectX + rectWidth / 2,
                    rectY + rectHeight / 2,
                    question.answers[answerIndex].text,
                    {
                        fontFamily: Fonts.Text.h3,
                        fontSize: Sizes.Text.h4,
                        fill: Colors.Text.Gray,
                        wordWrap: { width: 700 },
                        align: 'center',
                    }
                )
                .setAlpha(0)
                .setOrigin(0.5, 0.5); // Center horizontally and vertically
            this.answersOptionsText.push(this.answerText);
        });

        // Animate question text and index fadeIn
        this.tweens.add({
            targets: [this.questionText, this.QuestionIndexText, this.quizConsigne],
            alpha: 1,
            duration: 500,
            ease: 'Power2',
            onComplete: () => {
                this.answersOptions.forEach((option, i) => {
                    this.tweens.add({
                        targets: option,
                        alpha: 1,
                        scale: 1,
                        duration: 400,
                        delay: i * 100,
                        ease: 'Back.Out',
                    });
                    this.tweens.add({
                        targets: this.answersOptionsText[i],
                        alpha: 1,
                        duration: 400,
                        delay: i * 100,
                        ease: 'Power2',
                    });
                });
            },
        });

        // Add validation button
        this.submitButton = this.add
            .existing(
                new ButtonRounded(
                    this,
                    this.width - 997 / 2 - 100,
                    this.cameras.main.height - 200,
                    330,
                    76,
                    'SUBMIT',
                    Colors.Text.Blue,
                    Colors.Elmt.Yellow,
                    true,
                    Colors.Elmt.Yellow,
                    true,
                    false,
                    'fleche',
                    null
                )
            )
            .setScale(0)
            .setAlpha(0);

        // Add click event to the answer options
        this.answersOptions.forEach((option, index) => {
            option
                .setInteractive({ cursor: 'pointer' })
                .on('pointerdown', () => {
                    option.setFillStyle(Colors.Elmt.MediumBlue, 1);
                    this.answersOptionsText[index].setColor(Colors.Text.White);

                    this.tweens.add({
                        targets: this.submitButton,
                        alpha: 1,
                        scale: 1,
                        duration: 300,
                        ease: 'Back.Out',
                    });

                    this.answersOptions.forEach((opt, i) => {
                        if (i !== index) {
                            opt.setFillStyle(Colors.Elmt.White); // reset all backgrounds
                            this.answersOptionsText[i].setColor(Colors.Text.Gray); // reset all texts
                        }
                    });
                })
                .on('pointerover', () => {
                    // Only highlight if not already selected
                    if (option.fillColor !== Colors.Elmt.MediumBlue) {
                        option.setFillStyle(Colors.Elmt.LightBlue || 0xadd8e6, 1); // Use a light blue or fallback
                        this.answersOptionsText[index].setColor(Colors.Text.Blue || '#0056b3');
                    }
                })
                .on('pointerout', () => {
                    // Only reset if not already selected
                    if (option.fillColor !== Colors.Elmt.MediumBlue) {
                        option.setFillStyle(Colors.Elmt.White, 1);
                        this.answersOptionsText[index].setColor(Colors.Text.Gray);
                    }
                });
        });

        this.backSous = this.add.image(-250, 0, 'backSous').setOrigin(0, 0).setAlpha(0);

        this.debriefText = this.add
            .text(150, 250, question.debriefText, {
                fontFamily: Fonts.Text.h3,
                fontSize: Sizes.Text.h3,
                fill: Colors.Text.White,
                wordWrap: { width: 500 },
            })
            .setAlpha(0);

        this.debriefButton = this.add
            .existing(
                new ButtonRounded(
                    this,
                    150 + 330 / 2,
                    700,
                    330,
                    76,
                    'READ MORE',
                    Colors.Text.Blue,
                    Colors.Elmt.White,
                    true,
                    Colors.Elmt.White,
                    true,
                    false,
                    'fleche',
                    null
                )
            )
            .setAlpha(0);

        this.debriefButton.on('pointerdown', () => {
            this.tweens.add({
                targets: [this.debriefText, this.debriefButton, this.backSous],
                alpha: 0,
                duration: 100,
                onComplete: () => {
                    this.addDebriefScreen();
                    this.answersOptions.forEach((option) => option.setAlpha(0));
                    this.answersOptionsText.forEach((text) => text.setAlpha(0));
                    this.submitButton.setAlpha(0);
                    this.logo.destroy();
                },
            });
        });

        this.submitButton.on('pointerdown', () => {
            // Fade out robot, question index, and question text
            this.tweens.add({
                targets: [
                    this.robotQuestion,
                    this.QuestionIndexText,
                    this.questionText,
                    this.quizConsigne,
                    this.breadcrumb,
                ],
                alpha: 0,
                duration: 500,
                onComplete: () => {
                    // Fade in debrief text
                    this.tweens.add({
                        targets: [this.backSous, this.debriefText, this.debriefButton],
                        alpha: 1,
                        duration: 200,
                    });
                },
            });
            this.answersOptions.forEach((option) => {
                option.disableInteractive();
            });
            const selectedOptionIndex = this.answersOptions.findIndex(
                (option) => option.fillColor === Colors.Elmt.MediumBlue
            );
            if (selectedOptionIndex !== -1) {
                const selectedAnswer = question.answers[selectedOptionIndex];
                this.answersOptions[selectedOptionIndex].setFillStyle(Colors.Elmt.White, 1);
                this.answersOptionsText[selectedOptionIndex].setColor(Colors.Text.Gray);
                this.submitButton.setAlpha(0); // hide the submit button after click

                this.handleAnswerClick(selectedAnswer);
            } else {
                console.log('Please select an answer before submitting.');
            }
        });
    }

    goToQuestion(index) {
        // empêcher de cliquer pendant la transition
        this.input.enabled = false;

        // fade-out de la question actuelle
        this.tweens.add({
            targets: [
                this.questionText,
                this.QuestionIndexText,
                this.answersOptions,
                this.answersOptionsText,
                this.breadcrumb,
                this.robotQuestion,
                this.submitButton,
            ],
            alpha: 0,
            duration: 300,
            onComplete: () => {
                this.questionText.destroy();
                this.QuestionIndexText.destroy();
                this.answersOptions.forEach((option) => option.destroy());
                this.answersOptionsText.forEach((text) => text.destroy());
                this.robotQuestion.destroy();
                this.quizConsigne.destroy();
                this.submitButton.destroy();

                // mise à jour de l'index courant
                this.currentQuestionIndex = index;
                this.answersOptions = [];
                this.answersOptionsText = [];

                // mise à jour visuelle du breadcrumb
                this.breadcrumb.updateStep(index);

                // affichage de la nouvelle question
                const nextQuestion = quizQuestions[this.currentQuestionIndex];
                this.showQuestions(nextQuestion);

                // réactivation des interactions
                this.input.enabled = true;
            },
        });
    }

    handleAnswerClick(answer) {
        this.answersOptions.forEach((option, index) => {
            const isCorrect = quizQuestions[this.currentQuestionIndex].answers[index].isCorrect;
            if (isCorrect) {
                option.setStrokeStyle(4, Colors.Elmt.Green); // green outline for correct answer
                option.setFillStyle(Colors.Elmt.LightGreen, 1);
                this.answersOptionsText[index].setColor(Colors.Text.Gray);
            } else {
                if (!answer.isCorrect) {
                    option.setStrokeStyle(4, Colors.Elmt.Red);
                    option.setFillStyle(Colors.Elmt.DarkRed, 1);
                }
            }
        });

        // Add validation button
        /*  this.continueButton = this.add
      .existing(
        new ButtonRounded(
          this,
          this.width - 400,
          this.cameras.main.height - 500,
          86 * 2,
          32 * 2,
          "CONTINUE",
          Colors.Text.DarkBlue,
          Colors.Elmt.White,
          false,
          Colors.Text.White,
          false,
          false,
          "arrow-right",
          null
        )
      )
      .setAlpha(1);

    this.continueButton.on("pointerdown", () => {
      this.currentQuestionIndex++;
      if (this.currentQuestionIndex < quizQuestions.length) {
        this.clearPreviousQuestion();
        this.showQuestions(quizQuestions[this.currentQuestionIndex]);
      } else {
        console.log("Quiz completed!");
      }
    }); */
    }

    clearPreviousQuestion() {
        if (this.questionText) {
            this.questionText.destroy();
            this.questionText = null;
        }
        if (this.answersOptions) {
            this.answersOptions.forEach((option) => option.destroy());
            this.answersOptions = [];
        }
        if (this.answersOptionsText) {
            this.answersOptionsText.forEach((text) => text.destroy());
            this.answersOptionsText = [];
        }
    }

    addDebriefScreen() {
        const rectX = 50;
        const rectY = 50;
        const rectWidth = this.width - 100;
        const rectHeight = this.height - 100;
        const cornerRadius = 40;

        this.debriefScreenBackground = this.add
            .rexRoundRectangle(rectX, rectY, rectWidth, rectHeight, cornerRadius, Colors.Elmt.Gray)
            .setOrigin(0, 0)
            .setAlpha(0);

        const textXPosition = quizQuestions[this.currentQuestionIndex].textPosition[0];
        const textYPosition = quizQuestions[this.currentQuestionIndex].textPosition[1];
        const imageXPosition = quizQuestions[this.currentQuestionIndex].imagePosition[0];
        const imageYPosition = quizQuestions[this.currentQuestionIndex].imagePosition[1];

        this.debriefScreenText = this.add
            .image(
                textXPosition,
                textYPosition,
                quizQuestions[this.currentQuestionIndex].debriefScreenText
            )
            .setOrigin(1, 0) // Anchor to top-right corner
            .setAlpha(0);

        this.debriefScreenImage = this.add
            .image(
                imageXPosition, // Place at right edge minus 40px padding
                imageYPosition,
                quizQuestions[this.currentQuestionIndex].debriefScreenImage
            )
            .setOrigin(1, 0) // Anchor to top-right corner
            .setAlpha(0);

        // Add validation button
        const isLastQuestion = this.currentQuestionIndex === quizQuestions.length - 1;
        const buttonLabel = isLastQuestion ? 'CONTINUE' : 'NEXT QUESTION';
        this.continueButton = this.add
            .existing(
                new ButtonRounded(
                    this,
                    rectX + rectWidth - 330 / 2 - 40,
                    this.height / 2 + 350 + 50,
                    330,
                    76,
                    buttonLabel,
                    Colors.Text.Black,
                    Colors.Elmt.Yellow,
                    true,
                    Colors.Elmt.Yellow,
                    true,
                    false,
                    'fleche',
                    ''
                )
            )
            .setAlpha(0)
            .setDepth(99);

        this.continueButton.on('pointerdown', () => {
            this.tweens.add({
                targets: [
                    this.debriefScreenBackground,
                    this.debriefScreenText,
                    this.debriefScreenImage,
                    this.continueButton,
                ],
                alpha: 0,
                duration: 300,
                onComplete: () => {
                    this.debriefScreenBackground.destroy();
                    this.debriefScreenText.destroy();
                    this.debriefScreenImage.destroy();
                    this.continueButton.destroy();

                    this.currentQuestionIndex++;
                    if (this.currentQuestionIndex < quizQuestions.length) {
                        this.clearPreviousQuestion();
                        this.showQuestions(quizQuestions[this.currentQuestionIndex]);
                    } else {
                        this.cameras.main.fadeOut(500, 0, 0, 0); // Fade out over 500ms (black)
                        this.cameras.main.once('camerafadeoutcomplete', () => {
                            this.scene.restart();
                            this.scene.start('End');
                            this.game.data.activeIndex = 3; // Set active index to 2 after quiz completion
                            this.game.data.validatedIndex = this.game.data.activeIndex;
                        });
                    }
                },
            });
        });

        this.tweens.add({
            targets: [this.debriefScreenBackground],
            alpha: 0.5,
            duration: 500,
        });

        this.tweens.add({
            targets: [this.debriefScreenText, this.debriefScreenImage, this.continueButton],
            alpha: 1,
            duration: 500,
            /*  onComplete: () => {
        this.tweens.add({
          targets: this.continueButton,
          scale: 1,
          duration: 300,
          ease: "Back.Out",
        });
      }, */
        });
    }
}
